import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useAnalytics } from '../../../context/AnalyticsContext';
import { useAnalyticsEnabled } from '../../../context/FeatureFlagContext';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import InsightsIcon from '@mui/icons-material/Insights';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import PsychologyIcon from '@mui/icons-material/Psychology';
import CircularProgress from '@mui/material/CircularProgress';
import Switch from '@mui/material/Switch';
import AnalyticsPipelineSelector from './AnalyticsPipelineSelector';
import ModulesManagement from './ModulesManagement';
import './AnalyticsSection.css';

const AnalyticsSection = ({ isExpanded, onToggle, selectedInstrument, subscriptionStatus }) => {
  const isAnalyticsEnabled = useAnalyticsEnabled();
  const {
    setSelectedInstrument,
    activePipeline,
    pipelineModules,
    libraryModules,
    loadLibraryModules,
    removeModule,
    updateModule,
    openModuleConfigModal,
    lastResult,
    streamingModules,
    streamingProgress,
    analyzing,
    runAnalysisStreaming,
    cancelAnalysis,
    error,
    loading,
    isResultStale,
    resultAge,
  } = useAnalytics();

  const [modulesManagementOpen, setModulesManagementOpen] = useState(false);

  // Skeleton loader for loading state
  const SkeletonModuleItem = () => (
    <div className="module-item-skeleton">
      <div className="skeleton-icon"></div>
      <div className="skeleton-content">
        <div className="skeleton-title"></div>
        <div className="skeleton-subtitle"></div>
      </div>
    </div>
  );

  // Get current module being analyzed for nudge message
  const currentAnalyzingModule = useMemo(() => {
    if (!analyzing || !streamingProgress || streamingProgress.total === 0) return null;
    const enabledModules = pipelineModules.filter(m => m.enabled !== false);
    const currentIndex = streamingProgress.current;
    if (currentIndex < enabledModules.length) {
      const module = enabledModules[currentIndex];
      const libraryModule = libraryModules.find(l => l.id === module.libraryModuleId);
      return libraryModule?.name || module.libraryModuleId || null;
    }
    return null;
  }, [analyzing, streamingProgress, pipelineModules, libraryModules]);

  // Sync instrument with Analytics context
  useEffect(() => {
    if (selectedInstrument) {
      setSelectedInstrument(selectedInstrument);
    }
  }, [selectedInstrument, setSelectedInstrument]);

  // Load library modules on mount
  useEffect(() => {
    loadLibraryModules();
  }, [loadLibraryModules]);

  const handleAnalyze = useCallback(() => {
    if (analyzing) {
      cancelAnalysis();
    } else {
      // Each module uses its own configured timeframe
      runAnalysisStreaming();
    }
  }, [analyzing, cancelAnalysis, runAnalysisStreaming]);

  const handleRemoveModule = useCallback(async (moduleId) => {
    await removeModule(moduleId);
  }, [removeModule]);

  const handleToggleModule = useCallback(async (module) => {
    // enabled is true by default (undefined = enabled), toggle to opposite
    const currentEnabled = module.enabled !== false;
    await updateModule(module.id, { enabled: !currentEnabled });
  }, [updateModule]);

  const openModulesManagement = () => setModulesManagementOpen(true);
  const closeModulesManagement = () => setModulesManagementOpen(false);

  // Show banner instead of unmounting if disabled mid-session (preserves user's work)
  if (!isAnalyticsEnabled) {
    // If user was mid-analysis or had results, show a disabled banner
    if (analyzing || lastResult || pipelineModules.length > 0) {
      return (
        <div className="analytics-section conceptgate-section collapsed">
          <div className="analytics-disabled-banner">
            <InsightsIcon fontSize="small" />
            <span>Analytics feature has been disabled</span>
          </div>
        </div>
      );
    }
    return null;
  }

  // Use streamingModules as fallback if lastResult.moduleResults is empty
  const displayModules = analyzing
    ? streamingModules
    : (lastResult?.moduleResults?.length > 0 ? lastResult.moduleResults : streamingModules);

  const getDirectionClass = (direction) => {
    switch (direction) {
      case 'UP': return 'up';
      case 'DOWN': return 'down';
      default: return 'neutral';
    }
  };

  const directionIcon = lastResult?.direction === 'UP'
    ? <TrendingUpIcon className="direction-icon up" />
    : lastResult?.direction === 'DOWN'
      ? <TrendingDownIcon className="direction-icon down" />
      : <TrendingFlatIcon className="direction-icon neutral" />;

  // Count stats
  const enabledModules = pipelineModules.filter(m => m.enabled !== false);
  const totalModules = enabledModules.length;
  // Check both lastResult.moduleResults and streamingModules for results
  const hasResults = lastResult && (lastResult.moduleResults?.length > 0 || streamingModules.length > 0);

  // Compact status for collapsed view
  const getCompactStatus = () => {
    if (!hasResults) return null;
    const dir = lastResult.direction;
    if (dir === 'UP') {
      return (
        <span className="compact-status compact-up">
          <TrendingUpIcon fontSize="small" />
          BULLISH
        </span>
      );
    }
    if (dir === 'DOWN') {
      return (
        <span className="compact-status compact-down">
          <TrendingDownIcon fontSize="small" />
          BEARISH
        </span>
      );
    }
    return (
      <span className="compact-status compact-neutral">
        <TrendingFlatIcon fontSize="small" />
        NEUTRAL
      </span>
    );
  };

  return (
    <div className={`analytics-section conceptgate-section ${!isExpanded ? 'collapsed' : ''}`}>
      {/* Collapsed View */}
      <div className={`analytics-collapsed-view ${!isExpanded ? '' : 'hidden'}`}>
        <div className="analytics-collapsed-content" onClick={onToggle}>
          <div className="analytics-collapsed-header">
            <InsightsIcon fontSize="small" />
            <span className="analytics-title">Analytics</span>
            {activePipeline?.name && (
              <span className="pipeline-name-badge">{activePipeline.name}</span>
            )}
            {getCompactStatus()}
            {totalModules > 0 && (
              <span className="analytics-total-modules">{totalModules} module{totalModules !== 1 ? 's' : ''}</span>
            )}
          </div>
          <div className="analytics-compact-stats">
            {hasResults ? (
              <>
                <span className="analytics-stat">
                  <span className="stat-label">Certainty</span>
                  <span className="stat-value">
                    {((typeof lastResult.certainty === 'string' ? parseFloat(lastResult.certainty) : lastResult.certainty) * 100).toFixed(0)}%
                  </span>
                </span>
                <span className="analytics-stat">
                  <span className="stat-label">Strength</span>
                  <span className="stat-value">
                    {((typeof lastResult.strength === 'string' ? parseFloat(lastResult.strength) : lastResult.strength) * 100).toFixed(0)}%
                  </span>
                </span>
                {(lastResult.upVotes > 0 || lastResult.downVotes > 0) && (
                  <span className="analytics-stat">
                    <span className="stat-value vote-stat">
                      <span className="vote-up">{'\u2191'}{lastResult.upVotes}</span>
                      <span className="vote-down">{'\u2193'}{lastResult.downVotes}</span>
                    </span>
                  </span>
                )}
                {resultAge && (
                  <span className={`analytics-stat ${isResultStale ? 'stale' : ''}`}>
                    <span className="stat-value age">{resultAge}</span>
                  </span>
                )}
              </>
            ) : totalModules > 0 ? (
              <span className="analytics-stat">
                <span className="stat-label">Status</span>
                <span className="stat-value pending">Ready</span>
              </span>
            ) : (
              <span className="analytics-stat">
                <span className="stat-label">Status</span>
                <span className="stat-value empty">No modules</span>
              </span>
            )}
            {totalModules > 0 && (
              <div className="analytics-collapsed-action">
                <button
                  className={`analytics-analyze-btn compact ${analyzing ? 'analyzing' : ''}`}
                  onClick={(e) => { e.stopPropagation(); handleAnalyze(); }}
                  disabled={!selectedInstrument || analyzing}
                >
                  {analyzing ? (
                    <>
                      <CircularProgress size={14} color="inherit" />
                      <span>
                        {streamingProgress.total > 0
                          ? `${streamingProgress.current}/${streamingProgress.total}`
                          : '...'}
                      </span>
                    </>
                  ) : (
                    <>
                      <PlayArrowIcon fontSize="small" />
                      <span>Analyze</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
          <span className="collapse-toggle">
            <ExpandMoreIcon fontSize="small" />
          </span>
        </div>
      </div>

      {/* Expanded View */}
      <div className={`analytics-expanded-view ${isExpanded ? '' : 'hidden'}`}>
        {/* Section Header */}
        <div className="conceptgate-section-header" onClick={onToggle}>
          <InsightsIcon fontSize="small" />
          <span>Analytics Pipeline</span>
          <span className="pipeline-summary">
            {analyzing ? (
              <span className="summary-analyzing">Analyzing...</span>
            ) : hasResults ? (
              <span className={`summary-result ${getDirectionClass(lastResult.direction)}`}>
                {lastResult.direction === 'UP' ? 'Bullish' : lastResult.direction === 'DOWN' ? 'Bearish' : 'Neutral'}
              </span>
            ) : totalModules > 0 ? (
              <span className="summary-pending">{totalModules} modules</span>
            ) : (
              <span className="summary-empty">No modules</span>
            )}
          </span>
          <span className="collapse-toggle" title="Collapse">
            <ExpandLessIcon fontSize="small" />
          </span>
          <button
            className="pipeline-edit-btn"
            onClick={(e) => { e.stopPropagation(); openModulesManagement(); }}
            title="Add modules"
          >
            <AddIcon fontSize="small" />
          </button>
        </div>

        {/* Pipeline Selector */}
        <div className="analytics-section-content">
          <AnalyticsPipelineSelector subscriptionStatus={subscriptionStatus} />

          {/* Action Bar */}
          {pipelineModules.length > 0 && (
            <div className="analytics-action-bar">
              <div className="analytics-action-buttons">
                <button
                  className={`analytics-run-btn ${analyzing ? 'analyzing' : ''}`}
                  onClick={handleAnalyze}
                  disabled={!selectedInstrument || pipelineModules.length === 0}
                  title={pipelineModules.length === 0 ? 'Add modules to pipeline first' : hasResults ? 'Re-run analysis' : 'Run analysis'}
                >
                  {analyzing ? (
                    <>
                      <CircularProgress size={14} color="inherit" />
                      <span>
                        {streamingProgress.total > 0
                          ? `${streamingProgress.current}/${streamingProgress.total}`
                          : 'Starting...'}
                      </span>
                    </>
                  ) : hasResults ? (
                    <>
                      <PlayArrowIcon fontSize="small" />
                      <span>Re-analyze</span>
                    </>
                  ) : (
                    <>
                      <PlayArrowIcon fontSize="small" />
                      <span>Analyze</span>
                    </>
                  )}
                </button>
                {analyzing && (
                  <button
                    className="analytics-cancel-btn"
                    onClick={cancelAnalysis}
                    title="Cancel analysis"
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                )}
              </div>
              {analyzing && currentAnalyzingModule ? (
                <div className="analytics-nudge-text">
                  Analyzing {currentAnalyzingModule}...
                </div>
              ) : (
                <div className="analytics-disclaimer">
                  Each module uses its configured timeframe
                </div>
              )}
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="analytics-error">{error}</div>
          )}

          {/* Progress */}
          {analyzing && streamingProgress.total > 0 && (
            <div className="analytics-progress">
              <div
                className="analytics-progress-bar"
                style={{ width: `${(streamingProgress.current / streamingProgress.total) * 100}%` }}
              />
            </div>
          )}

          {/* Result Summary */}
          {lastResult && !analyzing && (
            <div className={`analytics-result ${isResultStale ? 'stale' : ''}`}>
              <div className={`analytics-direction ${getDirectionClass(lastResult.direction)}`}>
                {directionIcon}
                <span className="direction-label">
                  {lastResult.direction === 'UP' ? 'BULLISH' : lastResult.direction === 'DOWN' ? 'BEARISH' : 'NEUTRAL'}
                </span>
              </div>

              <div className="analytics-metrics">
                <div className="metric">
                  <span className="metric-value">{(lastResult.certainty * 100).toFixed(0)}%</span>
                  <span className="metric-label">Certainty</span>
                </div>
                <div className="metric">
                  <span className="metric-value">{(lastResult.strength * 100).toFixed(0)}%</span>
                  <span className="metric-label">Strength</span>
                </div>
              </div>

              <div className="analytics-votes">
                <span className="vote up">{'\u2191'} {lastResult.upVotes}</span>
                <span className="vote down">{'\u2193'} {lastResult.downVotes}</span>
                <span className="vote neutral">{'\u2194'} {lastResult.neutralVotes}</span>
              </div>

              {lastResult.summary && (
                <div className="analytics-summary">
                  {lastResult.summary}
                </div>
              )}

              {resultAge && (
                <div className={`analytics-age ${isResultStale ? 'stale' : ''}`}>
                  {isResultStale ? '⚠️ Stale results' : 'Analyzed'} {resultAge}
                </div>
              )}
            </div>
          )}

          {/* Pipeline Modules */}
          <div className="analytics-modules-section">
            <div className="modules-header">
              <span>Modules ({pipelineModules.length})</span>
              <button
                className="add-module-btn"
                onClick={openModulesManagement}
                title="Add module from library"
              >
                <AddIcon fontSize="small" />
              </button>
            </div>

            {/* Pipeline Module List - matches ValidationPipeline pattern */}
            {loading ? (
              <div className="analytics-skeleton-list">
                <SkeletonModuleItem />
                <SkeletonModuleItem />
                <SkeletonModuleItem />
              </div>
            ) : pipelineModules.length > 0 ? (
              <div className="conceptgate-gate-list">
                {pipelineModules.map((module, index) => {
                  // Find the corresponding result if we have one
                  // Match by multiple strategies (streaming sends index/moduleName, complete sends IDs)
                  const moduleIdStr = module.id?.toString();
                  const libraryModuleIdStr = module.libraryModuleId?.toString();
                  const libraryModule = libraryModules.find(l => l.id?.toString() === libraryModuleIdStr);
                  const moduleName = libraryModule?.name || module.moduleName;

                  // Calculate enabled index (streaming uses index among enabled modules only)
                  const enabledIndex = pipelineModules
                    .slice(0, index)
                    .filter(m => m.enabled !== false).length;
                  const isEnabled = module.enabled !== false;

                  const result = displayModules.find(r => {
                    // Strategy 1: Match by pipelineModuleId (from complete event or moduleResults)
                    const rPipelineModuleId = r.pipelineModuleId?.toString();
                    if (rPipelineModuleId && rPipelineModuleId === moduleIdStr) return true;

                    // Strategy 2: Match by libraryModuleId
                    const rLibraryModuleId = r.libraryModuleId?.toString();
                    if (rLibraryModuleId && rLibraryModuleId === libraryModuleIdStr) return true;

                    // Strategy 3: Match by index (streaming sends index among enabled modules)
                    if (isEnabled && r.index !== undefined && r.index === enabledIndex) return true;

                    // Strategy 4: Match by moduleName (fallback)
                    if (moduleName && r.moduleName === moduleName) return true;

                    return false;
                  });

                  // Get module config (timeframe, etc.)
                  let moduleConfig = {};
                  try {
                    moduleConfig = module.customConfigJson ? JSON.parse(module.customConfigJson) : {};
                  } catch (e) { /* ignore */ }
                  const timeframe = moduleConfig.timeframe || libraryModule?.defaultTimeframe || 'H1';
                  const moduleCategory = libraryModule?.category || 'GENERAL';
                  const isAiModule = moduleCategory === 'AI';

                  // Check if this module is currently being analyzed (among enabled modules)
                  // enabledIndex was calculated above for result matching
                  const isCurrentlyAnalyzing = analyzing &&
                    isEnabled &&
                    streamingProgress.total > 0 &&
                    !result &&
                    enabledIndex === streamingProgress.current;

                  // Get message similar to validation rules
                  const getMessage = () => {
                    if (!isEnabled) return 'Disabled';
                    if (isCurrentlyAnalyzing) return 'Analyzing...';
                    if (result?.message) return result.message;
                    if (result) {
                      const dirLabel = result.direction === 'UP' ? 'Bullish' : result.direction === 'DOWN' ? 'Bearish' : 'Neutral';
                      // Parse certainty as number (may be string from backend)
                      const certaintyVal = typeof result.certainty === 'string' ? parseFloat(result.certainty) : result.certainty;
                      const certaintyPct = certaintyVal ? (certaintyVal * 100).toFixed(0) : '?';
                      return `${dirLabel} signal with ${certaintyPct}% certainty`;
                    }
                    return 'Pending analysis';
                  };

                  const isErrorResult = result?.status === 'ERROR';

                  // Get status class for styling
                  const getStatusClass = () => {
                    if (!isEnabled) return 'gate-disabled';
                    if (isCurrentlyAnalyzing) return 'gate-analyzing';
                    if (result) {
                      if (isErrorResult) return 'gate-error';
                      if (result.direction === 'UP') return 'gate-passed';
                      if (result.direction === 'DOWN') return 'gate-failed';
                      return 'gate-neutral';
                    }
                    return 'gate-pending';
                  };

                  // Get status icon
                  const getStatusIcon = () => {
                    if (!isEnabled) {
                      return <span className="gate-icon disabled">{'\u23F8'}</span>;
                    }
                    if (isCurrentlyAnalyzing) {
                      return <CircularProgress size={18} className="gate-icon validating" />;
                    }
                    if (result) {
                      if (isErrorResult) return <span className="gate-icon error">{'\u26A0'}</span>;
                      if (result.direction === 'UP') return <TrendingUpIcon className="gate-icon passed" fontSize="small" />;
                      if (result.direction === 'DOWN') return <TrendingDownIcon className="gate-icon failed" fontSize="small" />;
                      return <TrendingFlatIcon className="gate-icon neutral" fontSize="small" />;
                    }
                    return <span className="gate-icon pending">{'\u23F3'}</span>;
                  };

                  return (
                    <div
                      key={module.id}
                      className={`conceptgate-gate-item ${getStatusClass()}`}
                    >
                      <div className="conceptgate-gate-header">
                        <div className="gate-status-icon">
                          {getStatusIcon()}
                        </div>
                        <div className="gate-info">
                          <div className="gate-name-row">
                            <span className="gate-label">
                              {libraryModule?.name || module.libraryModuleId}
                            </span>
                            <span className="gate-type-badge">
                              {isAiModule && <PsychologyIcon fontSize="inherit" />}
                              {timeframe}
                            </span>
                            {result && (
                              <span className={`module-certainty-badge ${getDirectionClass(result.direction)}`}>
                                {((typeof result.certainty === 'string' ? parseFloat(result.certainty) : result.certainty) * 100).toFixed(0)}%
                              </span>
                            )}
                          </div>
                          <span className="gate-message">{getMessage()}</span>
                        </div>
                        <div className="gate-actions">
                          <Switch
                            size="small"
                            checked={isEnabled}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleToggleModule(module);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="gate-toggle-switch"
                            title={isEnabled ? 'Disable module' : 'Enable module'}
                          />
                          <button
                            className="gate-action-btn gate-edit-btn"
                            onClick={(e) => { e.stopPropagation(); openModuleConfigModal(module, libraryModule); }}
                            title="Configure module"
                          >
                            <EditIcon fontSize="small" />
                          </button>
                          <button
                            className="gate-action-btn gate-remove-btn"
                            onClick={(e) => { e.stopPropagation(); handleRemoveModule(module.id); }}
                            title="Remove module"
                          >
                            <CloseIcon fontSize="small" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="pipeline-empty">
                <InsightsIcon className="empty-icon" />
                <p className="empty-title">No analysis modules yet</p>
                <p className="empty-description">
                  Add modules to analyze market conditions before your trades
                </p>
                <button className="add-rules-btn" onClick={openModulesManagement}>
                  <AddIcon fontSize="small" />
                  Add Modules
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modules Management Modal */}
      <ModulesManagement
        open={modulesManagementOpen}
        onClose={closeModulesManagement}
        subscriptionStatus={subscriptionStatus}
      />
    </div>
  );
};

export default AnalyticsSection;
